import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { globalShortcutRegistry } from './GlobalShortcutRegistry';
import type { FocusZone, ShortcutDefinition, ShortcutScope } from './types';

interface KeyboardContextValue {
  scope: ShortcutScope;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  focusZone: FocusZone;
  setFocusZone: (zone: FocusZone) => void;
  statusHint: string;
  setStatusHint: (hint: string) => void;
  registerShortcut: (def: ShortcutDefinition) => () => void;
}

const KeyboardShortcutContext = createContext<KeyboardContextValue | null>(null);

interface Props {
  children: ReactNode;
  scope?: ShortcutScope;
}

const POS_TERMINAL_SCOPES = new Set<ShortcutScope>(['pos', 'pos-held']);

function shouldCaptureFunctionKey(scope: ShortcutScope, key: string): boolean {
  if (!POS_TERMINAL_SCOPES.has(scope)) return false;
  return /^f([1-9]|1[01])$/i.test(key);
}

export function KeyboardShortcutProvider({ children, scope = 'pos' }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [focusZone, setFocusZone] = useState<FocusZone>('product');
  const [statusHint, setStatusHint] = useState('');

  const registerShortcut = useCallback((def: ShortcutDefinition) => {
    return globalShortcutRegistry.register({ ...def, scope: def.scope ?? scope });
  }, [scope]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (shouldCaptureFunctionKey(scope, e.key)) {
        e.preventDefault();
      }
      globalShortcutRegistry.handle(e, { modalOpen, scope });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [modalOpen, scope]);

  const value = useMemo(
    () => ({
      scope,
      modalOpen,
      setModalOpen,
      focusZone,
      setFocusZone,
      statusHint,
      setStatusHint,
      registerShortcut,
    }),
    [scope, modalOpen, focusZone, statusHint, registerShortcut],
  );

  return (
    <KeyboardShortcutContext.Provider value={value}>{children}</KeyboardShortcutContext.Provider>
  );
}

export function useKeyboardContext() {
  const ctx = useContext(KeyboardShortcutContext);
  if (!ctx) throw new Error('useKeyboardContext requires KeyboardShortcutProvider');
  return ctx;
}
